/**
 * TicketsTable - A project's tickets with infinite scroll
 *
 * Features:
 * - One row per ticket, grouping every run of it
 * - Latest run's status and acceptance status, run count, total cost (admin)
 * - Click opens the ticket page, or the run itself when there is only one
 * - Link to Jira ticket
 */

import { Loader2, ExternalLink, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TicketSummary } from '@resolve-ai/shared';
import { StatusBadge } from '../common';
import { AcceptanceStatus } from './AcceptanceStatus';
import { formatRelativeTime } from '@/lib/date-utils.ts';
import { formatCost } from '@/lib/format-utils.ts';
import { ticketPath } from '@/lib/tickets-api.ts';

interface TicketsTableProps {
  tickets: TicketSummary[];
  projectId: string;
  showCost: boolean;
  isLoadingMore: boolean;
  isSearching: boolean;
  hasMore: boolean;
  sentinelRef: (node: HTMLDivElement | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onCancelTask: (taskId: string) => void;
}

function rowPath(projectId: string, ticket: TicketSummary): string {
  const hasSingleRun = ticket.runCount === 1;
  return hasSingleRun
    ? `/projects/${projectId}/bugs/${ticket.latestRun.id}`
    : ticketPath(projectId, ticket.ticketKey);
}

export function TicketsTable({
  tickets,
  projectId,
  showCost,
  isLoadingMore,
  isSearching,
  hasMore,
  sentinelRef,
  searchQuery,
  onSearchChange,
  onCancelTask,
}: TicketsTableProps) {
  const hasSearchQuery = searchQuery.trim().length > 0;
  const showEmptyState = tickets.length === 0 && !isSearching;
  const noSearchResults = showEmptyState && hasSearchQuery;
  const noTicketsYet = showEmptyState && !hasSearchQuery;

  return (
    <div>
      <div className="mb-4">

        {/* Search Input - Right Side */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ticket key..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="dark:text-white pl-9 pr-4 py-2 w-72 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
          )}
        </div>
      </div>

      {noTicketsYet && (
        <p className="text-gray-600 dark:text-gray-400">No tickets yet</p>
      )}

      {noSearchResults && (
        <p className="text-gray-600 dark:text-gray-400">No tickets found for &quot;{searchQuery}&quot;</p>
      )}

      {tickets.length > 0 && (
        <>
          <div className="overflow-x-auto  border rounded-lg border-[#D9D9D9]">
            <table className="w-full task-table">
              <thead>
                <tr className="border-b text-left font-body-sm capitalize text-gray-500 dark:text-white-500 border-gray-200 dark:border-gray-700">
                  <th className="w-44">
                    Ticket
                  </th>
                  <th colSpan={3} className="">
                    Title
                  </th>
                  <th className="w-44">
                    Status
                  </th>
                  <th className="w-48">
                    Acceptance Status
                  </th>
                  <th className="w-20">
                    Runs
                  </th>
                  {showCost && (
                    <th className="w-28">
                      Total Cost
                    </th>
                  )}
                  <th className="w-44">
                    Last Run
                  </th>
                  <th className="w-24">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className={'text-gray-900 dark:text-white '}>
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.ticketKey}
                    className="group hover:bg-[#F5F5F5] font-body-base dark:hover:bg-[#444444] cursor-pointer transition-colors"
                    onClick={() => {
                      window.open(rowPath(projectId, ticket), '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <td className="w-44">
                      <div className="flex items-center gap-2">
                        <Link
                          to={ticket.ticketUrl || '#'}
                          className="hover:underline"
                          onClick={(e) => e.stopPropagation()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {ticket.ticketKey}
                        </Link>
                        <ExternalLink className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                    </td>
                    <td colSpan={3} className="">
                      <p className=" truncate max-w-md">
                        {ticket.ticketTitle || 'No title'}
                      </p>
                    </td>
                    <td className="w-44">
                      <span><StatusBadge showDot={false} status={ticket.latestRun.status} size="lg" /></span>
                    </td>
                    <td className="w-48">
                      <AcceptanceStatus
                        prReviewStatus={ticket.latestRun.prReviewStatus}
                        noPrReviewStatus={ticket.latestRun.noPrReviewStatus}
                      />
                    </td>
                    <td className="w-20">
                      {ticket.runCount}
                    </td>
                    {showCost && (
                      <td className="w-28">
                        {ticket.totalCostCents === null ? '--' : formatCost(ticket.totalCostCents)}
                      </td>
                    )}
                    <td className="w-44">
                      {formatRelativeTime(ticket.lastRunAt)}
                    </td>
                    <td className="w-24">
                      {ticket.latestRun.status === 'pending' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCancelTask(ticket.latestRun.id);
                          }}
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sentinel for intersection observer */}
          <div ref={sentinelRef} className="h-1" aria-hidden="true" />

          {/* Loading indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading more...</span>
            </div>
          )}

          {/* End of list */}
          {!hasMore && tickets.length > 0 && !isLoadingMore && (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">All tickets loaded</p>
          )}
        </>
      )}
    </div>
  );
}
